/** @jsx jsx */
import { Box, Container, Flex } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import { FC, useCallback, useContext } from "react";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { VALIDATION_TYPES } from "@/components/Formik";
import { UserContext } from "@/components/User";
import { maxJobApplicationStepKey } from "@/data/job-applications/utils/maxJobApplicationStepKey";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { countDecimals } from "@/utils/numbers/countDecimals";

import { ApplyWizardBackButton } from "../_components/ApplyWizardBackButton";
import { useApplyWizard } from "../_components/ApplyWizardContext";
import { ApplyDetailsForm, DetailsFormValues, detailsValidation, transformDraftWithDetails } from "./ApplyDetailsForm";

interface ApplyWizardDetailsProps {
  jobRequest: JobRequest;
}

export const ApplyWizardDetails: FC<ApplyWizardDetailsProps> = ({ jobRequest }) => {
  const User = useContext(UserContext);
  const { contextConsultantProfile } = useContext(UserContext);

  const { createOrUpdateDraft, goToNextStep, profile, draft } = useApplyWizard();

  let finalValidation = detailsValidation;
  const validateCosts =
    (jobRequest?.payExpressConfig?.payExpressEnabled && Boolean(jobRequest?.paymentTerm) && User.contextCompany) ||
    jobRequest?.brokerFee?.percentage;
  if (validateCosts) {
    const costsValidation = Yup.object()
      .required()
      .shape({
        costs: Yup.object().shape({
          finalRatePerHour: Yup.number()
            .transform((value, originalValue) => (/\s/.test(originalValue) ? 0 : value))
            .nullable()
            .required(VALIDATION_TYPES.MORE_THAN)
            .typeError(VALIDATION_TYPES.NUMBERS)
            .test({
              name: "two-decimal-places",
              test: (v: number) => countDecimals(v) <= 2,
              message: VALIDATION_TYPES.MAX_2_DECIMAL_PLACES,
            })
            .max(999999999999999, VALIDATION_TYPES.TOO_BIG_NUMBER)
            .moreThan(0, VALIDATION_TYPES.MORE_THAN),
        }),
      });
    finalValidation = finalValidation.concat(costsValidation);
  }

  const handleSubmit = useCallback(
    async (values: DetailsFormValues) => {
      try {
        await createOrUpdateDraft(draft => ({
          ...transformDraftWithDetails(values)(draft),
          step: maxJobApplicationStepKey(draft?.step, "DETAILS"),
        }));
      } catch (error) {
        // NOTE: errors are ignored on purpose
      } finally {
        goToNextStep();
      }
    },
    [createOrUpdateDraft, goToNextStep],
  );

  return (
    <Box py="md">
      <Container variant="narrow">
        <Flex px="sm" flexDirection="column" gap="sm">
          <ApplyWizardBackButton />
          <Formik
            initialValues={{
              motivation: draft?.motivation ?? "",
              rate: {
                ratePerHour: draft?.rate?.ratePerHour as number,
                currency: draft?.rate?.currency || jobRequest.rate.currency,
              },
              contactPerson: draft?.consultantProfile.contactPerson || contextConsultantProfile!,
              availability: draft?.consultantProfile.availability || profile!.availability!,
              availabilityDate:
                draft?.consultantProfile.availability === "SPECIFIC_DATE"
                  ? draft.consultantProfile.availabilityDate
                  : !draft?.consultantProfile.availability && profile!.availability === "SPECIFIC_DATE"
                  ? profile!.availabilityDate
                  : null,
              payExpressSelected: draft?.payExpressSelected || false,
              costs: draft?.costs ?? {
                payExpressFeeAmount: 0,
                payExpressFeePercentage: 0,
                finalRatePerHour: 0,
                brokerFeePercentage: 0,
                brokerFeeAmount: 0,
              },
            }}
            validationSchema={finalValidation}
            onSubmit={handleSubmit}>
            {(formikProps: FormikProps<DetailsFormValues>) => (
              <ApplyDetailsForm formik={formikProps} jobRequest={jobRequest} />
            )}
          </Formik>
        </Flex>
      </Container>
    </Box>
  );
};
